import { Dispatch, useEffect, useMemo, useReducer, useState } from "react";
import { Outlet, useLocation, useOutletContext } from "react-router";
import { gql } from "@apollo/client";
import { useMutation } from "@apollo/client/react";
import { defineMessages, useIntl } from "react-intl";
import {
  IconAddressBook,
  IconShoppingCart,
  IconSquareRoundedChevronsLeftFilled,
  IconSquareRoundedChevronsRightFilled,
  IconWallet
} from "@tabler/icons-react";
import { useAppConfig } from "@ct-react/core";
import { useLocaleNavigate, useLocaleUrl } from "@ct-react/locale";
import { CartPaymentMode } from "@ct-react/calendar";
import { useVisitor } from "@ct-react/visitor";
import { CustomerInput } from "@shared/graphql";
import { useTools } from "../../hooks/tools";
import { useLazyCartCount } from "../../hooks/cart";
import { Wrapper } from "../../components/layout/wrapper";
import Stepper from "../../components/cart/stepper";
import Button from "../../components/common/button";
import "./common.scss";

const transDefs = defineMessages({
  cart: { id: "cart.stepper.cart.title", defaultMessage: "Panier" },
  cartNext: { id: "cart.stepper.cart.next", defaultMessage: "Réserver maintenant" },
  info: {id: "cart.stepper.info.title", defaultMessage: "Vos informations" },
  infoPrevious: { id: "cart.stepper.info.previous", defaultMessage: "Modifier votre réservation" },
  infoNext: { id: "cart.stepper.info.next", defaultMessage: "Définir le modèle de paiement" },
  payment: { id: "cart.stepper.payment.title", defaultMessage: "Paiement" },
  paymentPrevious: { id: "cart.stepper.payment.previous", defaultMessage: "Modifier vos informations" },
  paymentNext: { id: "cart.stepper.payment.next", defaultMessage: "Payer et finaliser la réservation" }
});

export const FULL_CART_GQL_DATA = gql`
  query UserCart($sessionId: String, $userId: String) {
    cart(sessionId: $sessionId, userId: $userId) {
      cartId,
      items {
        ... on RentalAccommodationCartItem {
          itemId
          articleId
          title description image period
          price { bare discount options { type amount } amount }
          downPayment { rate providerCharge }
          expirationDate
        }
      }
      fullPrice { amount discount }
    }
  }
`;

export const CART_ITEM_DETAIL_GQL_DATA = gql`
  query UserCartItemComplement($articleId: ID!) {
    detail: articleDetail(articleId: $articleId) { ... on RentalAccommodationArticle { technicalName } }
  }
`;

const CART_CHECKOUT_GQL_MUT = gql`
  mutation CartCheckout($sessionId: String, $userId: String, $checkout: CheckoutCartInput!) {
    cartCheckout(sessionId: $sessionId, userId: $userId, checkout: $checkout) @recaptcha(action: "checkout") { paymentUrl }
  }
`;

type StepInfo = { index: 0 | 1 | 2 | 3, valid: boolean, maskActions?: true };

type Context = {
  step: StepInfo;
  sessionId: string;
  userId?: string;
  statData: any;
  customer?: CustomerInput;
  paymentMode?: CartPaymentMode;
}

type Action =
  { type: "save.items.data", payload: any } |
  { type: "change.index", payload: StepInfo } |
  { type: "change.customer.contact", payload?: CustomerInput } |
  { type: "change.payment.mode", payload?: "full" | "partial" };

const reducer = (state: Context, action: Action): Context => {
  switch (action.type) {
    case "save.items.data":
      return { ...state, statData: action.payload };
    case "change.index":
      return { ...state, step: action.payload };
    case "change.customer.contact":
      return { ...state, customer: action.payload };
    case "change.payment.mode":
      return { ...state, paymentMode: action.payload };
    default:
      return state;
  }
}

const initialState = (): Context => {
  const { sessionId, userId } = useVisitor();
  const { state } = useLocation();
  return {
    step: { index: 0, valid: true },
    sessionId,
    userId,
    statData: undefined,
    ...(!!state?.customer) && { customer: state.customer }
  };
}

export const useCheckoutContext = () => useOutletContext<[ state: Readonly<Context>, dispatch: Dispatch<Action> ]>();

const CheckoutOutlet = () => {

  const intl = useIntl();
  const { baseUrl } = useAppConfig();
  const { buildLocaleHref } = useLocaleUrl();
  const navigate = useLocaleNavigate();
  const validCart = useLazyCartCount("no-cache");
  const { gtm, mutation } = useTools();

  const [ asyncProcessing, setAsyncProcessing ] = useState<boolean>(false);
  const [ state, dispatch ] = useReducer(reducer, initialState());

  const [ checkout ] = useMutation<any>(CART_CHECKOUT_GQL_MUT, {
    variables: {
      sessionId: state.sessionId,
      userId: state.userId
    }
  });

  const stepConfig = [
    { icon: IconShoppingCart, msg: transDefs.cart },
    { icon: IconAddressBook, msg: transDefs.info },
    { icon: IconWallet, msg: transDefs.payment }
  ];

  const navigation = useMemo(() => {
    switch (state.step.index) {
      case 1:
        return {
          next: {
            label: transDefs.cartNext,
            disabled: !state.step.valid,
            action: () => navigate("/checkout/information")
          }
        }
      case 2:
        return {
          previous: {
            label: transDefs.infoPrevious,
            action: () => navigate("/checkout/")
          },
          next: {
            label: transDefs.infoNext,
            disabled: !state.step.valid,
            action: () => navigate("/checkout/payment/"),
          }
        }
      case 3:
        return {
          previous: {
            label : transDefs.paymentPrevious,
            action: () => navigate("/checkout/information")
          },
          next: {
            label: transDefs.paymentNext,
            disabled: !state.step.valid,
            action: async () => {
              setAsyncProcessing(true);
              await validCart()
                .then(() => mutation.withRecaptcha(checkout, "checkout")({
                  variables: {
                    checkout: {
                      customer: state.customer,
                      hasFullPayment: state.paymentMode === "full",
                      redirect: {
                        baseUrl,
                        successPath: buildLocaleHref("/checkout/payment/success"),
                        cancelPath: buildLocaleHref("/checkout/payment/cancellation")
                      }
                    }
                  }
                }))
                .then(({ data: { cartCheckout } }) => {
                  !!state.statData && gtm.sendEvent({
                    event: "purchase",
                    ecommerce: { ...state.statData, payment_mode: state.paymentMode }
                  });
                  return cartCheckout;
                })
                .then(checkout => !checkout.paymentUrl
                  ? navigate("/cart/payment/success")
                  : window.location.href = checkout.paymentUrl)
                .finally(() => setAsyncProcessing(false));
            }
          }
        }
      default:
        return { previous: undefined, next: undefined };
    }
  }, [ state ]);

  return (
    <>
      <meta name="robots" content="noindex" />
      <Wrapper.Centered className="cart-progress">
        <Stepper steps={stepConfig} activeIndex={state.step.index} />
      </Wrapper.Centered>
      <Wrapper.Centered as="main" className="cart-wrap">
        <Outlet context={[ state, dispatch ]} />
      </Wrapper.Centered>
      {(!!navigation && !state.step.maskActions) &&
        <Wrapper.Centered className="cart-actions">
          <div className="previous">
            {!!navigation.previous &&
              <Button className="outline bold"
                      onClick={() => navigation.previous.action()}>
                <IconSquareRoundedChevronsLeftFilled className="a-icon" />
                {intl.formatMessage(navigation.previous.label)}
              </Button>
            }
          </div>
          <div className="next">
            {!!navigation.next &&
              <Button className="bold primary"
                      processing={asyncProcessing}
                      disabled={navigation.next.disabled}
                      onClick={() => navigation.next.action()}>
                {intl.formatMessage(navigation.next.label)}
                <IconSquareRoundedChevronsRightFilled />
              </Button>
            }
          </div>
        </Wrapper.Centered>
      }
    </>);

}

export default CheckoutOutlet;
